<template>
  <div
    class="marquee-container"
    :style="{
      '--bg': data.background_color,
      '--color': data.color,
    }"
  >
    <div
      :class="['marquee-overflow', !loaded ? 'marquee-loading' : false]"
      :style="{
        '--marquee-width': `${totalWidth}px`,
        '--marquee-duration': `${duration}s`,
        '--marquee-direction': direction > 0 ? 'reverse' : undefined,
      }"
    >
      <div
        :class="['marquee-wrapper', switchDuplicate ? 'marquee-wrapper-start' : false]"
      >
        <div
          ref="marquee"
          class="marquee"
          :data-direction="data.direction"
          align="center"
        >
          <div
            v-for="wrapper in wrappers"
            :key="wrapper"
            class="item-wrapper"
          >
            <Flex
              v-for="(item, index) in data.items"
              :key="index"
              :class="['item', `item--${item.type}`]"
              align="center"
            >
              <Spacer
                x="s"
                top="1"
              >
                <Cta
                  v-if="item.type === 'text' && item.url"
                  :data="{
                    title: item.text,
                    url: item.url,
                    size: 'label-s'
                  }"
                />
                <Title
                  v-else-if="item.type === 'text' && !item.url"
                  :data="{
                    value: item.text,
                    size: 'label-s'
                  }"
                />
                <Figure
                  v-else
                  ref="img"
                  :skip-load="!switchDuplicate && !isResizing"
                  :data="{...item.image, lazyload: false, ratio: false, object_fit: 'contain', relative: true }"
                />
              </Spacer>
            </Flex>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Cta from '@/components/typo/cta';
import Title from '@/components/typo/title';
import Figure from '@/components/media/figure';

export default {
  name: 'Marquee',
  components: {
    Cta,
    Figure,
    Title,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loaded: false,
      direction: this.data.direction === 'ltr' ? -1 : 1,
      switchDuplicate: false,
      duplicateItems: [],
      totalWidth: 0,
      delta: 0,
      x: 0,
      speed: 16,
      isResizing: false,
      wrappers: 2,
    };
  },
  computed: {
    duration() {
      return ((this.speed / 10) * this.totalWidth) / 100;
    },
  },
  watch: {
    loaded(val) {
      if (val) {
        this.setDuplicateItems();
      }
    },
    totalWidth(val) {
      if (val === 0) this.setTotalWidth();
    },
  },
  mounted() {
    this.promises().then(() => {
      this.setTotalWidth();
      this.loaded = true;
    });

    this.$bus.$on('windowResized', this.resize);
  },
  beforeDestroy() {
    this.stop();
  },
  methods: {
    setTotalWidth() {
      if (this.$refs.marquee && this.$refs.marquee && this.$refs.marquee.children) {
        this.totalWidth = this.$refs.marquee.children[0].offsetWidth;
      }
    },
    setDuplicateItems() {
      this.delta = (Math.floor(window.innerWidth / this.totalWidth) + 2);
      this.wrappers = this.delta < 2 ? 2 : this.delta;
      this.start();
    },
    start() {
      this.switchDuplicate = true;
    },
    stop() {
      this.switchDuplicate = false;
    },
    resize() {
      this.switchDuplicate = false;
      this.loaded = false;
      this.isResizing = true;

      this.$nextTick(() => {
        this.setTotalWidth();
        this.loaded = true;
        this.isResizing = false;
      });
    },
    promises() {
      const promises = [];
      if (this.$refs.img && this.$refs.img.length > 0) {
        this.$refs.img.forEach((img) => {
          promises.push(img.load());
        });
      }

      return Promise.all(promises);
    },
  },
};
</script>

<style lang="scss" scoped>
.marquee {
  &-container {
    background: var(--bg, var(--grey-d));
    color: var(--color, var(--white));
  }
  &-overflow {
    position: relative;
    overflow: hidden;
  }

  &-loading {
    opacity: 0;
  }

  &-wrapper {
    width: var(--marquee-width);
    white-space: nowrap;

    animation-name: marquee;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    animation-play-state: paused;
    animation-duration: var(--marquee-duration);
    animation-direction: var(--marquee-direction, initial);

    &-start {
      animation-play-state: running;
    }

    &:hover {
      animation-play-state: paused;
    }

    display: inline-block;
  }

  flex-wrap: nowrap;
  white-space: nowrap;
  width: max-content;
  display: inline-block;

  .item-wrapper {
    white-space: nowrap;
    display: inline-block;
  }

  .item {
    height: var(--bar-height);
    white-space: nowrap;
    @extend %typo--s-alt;

    &--img {
      max-width: 300px;
    }
  }
}

@keyframes marquee {
  0% { transform: translate3d(0%, 0, 0); }
  100% { transform:translate3d(-100%, 0, 0); }
}
</style>
